import {ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit} from '@angular/core';
import {AuthenticatedBaseComponent} from '../../../authenticated-base.component';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {EditModeEnum} from '../../../../enums/EditModeEnum';
import {Backend, PERMISSION} from '@generated-backend-types';
import {StrictFormGroup} from '@browsepedia/ng-forms';
import {FormControl, NonNullableFormBuilder, ReactiveFormsModule, Validators} from '@angular/forms';
import {ContentHeaderService} from '../../../../services/content-header.service';
import {TitleService} from '../../../../services/title.service';
import {of, switchMap, takeUntil, throwError} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {FormGroupComponent} from '../../../../components/form-inputs/form-group/form-group.component';
import {NotEnoughPermissionsError} from '../../../../errors/NotEnoughPermissionsError';
import {HttpErrorResponse} from '@angular/common/http';
import {UserSessionService} from '../../../../services/user-session.service';
import {ToastrService} from 'ngx-toastr';
import {FormErrorService} from '../../../../services/form-error.service';
import {DeleteCardComponent} from '../../../../components/form-inputs/delete-card/delete-card.component';
import {IfHasAllPermissionsDirective} from '../../../../directives/if-has-all-permissions.directive';
import {DiagnosisService} from '../../../../services/api/diagnosis.service';
import {StateCardComponent} from '../../../../components/state-card/state-card.component';
import {
  MultiSelectWithServiceListComponent
} from '../../../../components/form-inputs/multi-select-with-service-list/multi-select-with-service-list.component';
import {EmailService} from '../../../../services/api/administration/email.service';
import ModelStateEnum = Backend.App.Enums.ModelStateEnum;
import DiagnosisData = Backend.App.Data.Dashboard.Administration.DiagnosisData;
import Diagnosis = Backend.App.Resources.Dashboard.Administration.Diagnosis;
import EmailTypeEnum = Backend.App.Enums.Administration.EmailTypeEnum;

@Component({
  selector: 'app-authenticated-administration-emails-edit',
  templateUrl: './edit.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    FormGroupComponent,
    DeleteCardComponent,
    IfHasAllPermissionsDirective,
    StateCardComponent,
    MultiSelectWithServiceListComponent,
  ]
})
class EditComponent extends AuthenticatedBaseComponent implements OnInit {
  public diagnosis?: Diagnosis;
  public mode = EditModeEnum.CREATE;

  public form: StrictFormGroup<DiagnosisData> = this.fb.group({
    state: this.fb.control(ModelStateEnum.DRAFT, [Validators.required]),
    title: this.fb.group({
      de: new FormControl('', [Validators.required]),
      en: new FormControl('', [Validators.required]),
      ru: new FormControl('', [Validators.required]),
    }),
    parent_id: this.fb.control(''),
    automatic_acceptance: this.fb.control(false, [Validators.required]),
    automatic_acceptance_email_id: this.fb.control(''),
    contact_form_slug: this.fb.control('', [Validators.pattern(/^[a-z0-9]+(?:-[a-z0-9]+)*$/), Validators.maxLength(100)]),
  });
  protected readonly PERMISSION = PERMISSION;
  protected readonly ModelStateEnum = ModelStateEnum;
  protected readonly EmailTypeEnum = EmailTypeEnum;

  public constructor(
    protected override readonly cdr: ChangeDetectorRef,
    protected override readonly translate: TranslateService,
    protected override readonly contentHeaderService: ContentHeaderService,
    protected override readonly titleService: TitleService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly userSessionService: UserSessionService,
    private readonly toastrService: ToastrService,
    private readonly formErrorService: FormErrorService,
    private readonly router: Router,
    private readonly fb: NonNullableFormBuilder,
    protected readonly diagnosisService: DiagnosisService,
    protected emailService: EmailService,
  ) {
    super(cdr, translate, contentHeaderService, titleService);
  }

  public ngOnInit() {
    this.activatedRoute.data.pipe(takeUntil(this.onDestroy$)).subscribe(data => {
      const diagnosis: Diagnosis | undefined = data['diagnosis'];

      if (diagnosis) {
        this.diagnosis = diagnosis;

        this.form.patchValue(diagnosis);
        this.mode = EditModeEnum.EDIT;
        this.cdr.markForCheck();
      }

      this.titleService.setTitle('authenticated.administration.diagnoses.' + this.mode + '.title');
    });

    this.form.controls.automatic_acceptance.valueChanges.subscribe(newValue => {
      this.form.controls.automatic_acceptance_email_id.setValidators(newValue ? [Validators.required] : []);
      this.form.controls.automatic_acceptance_email_id.setValue(null);
      this.form.controls.automatic_acceptance_email_id.markAsUntouched();
    });
  }

  public save() {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    if (!this.form.valid) {
      return;
    }

    const data = this.form.value as DiagnosisData;

    of(this.diagnosis?.id).pipe(
      switchMap(id => {
        if (id) {
          if (!this.userSessionService.hasAllPermission([PERMISSION.ADMINISTRATION_DIAGNOSIS_UPDATE_ALL])) {
            throwError(() => new NotEnoughPermissionsError());
          }

          return this.diagnosisService.update(id, data);
        }

        if (!this.userSessionService.hasAllPermission([PERMISSION.ADMINISTRATION_DIAGNOSIS_CREATE_ALL])) {
          throwError(() => new NotEnoughPermissionsError());
        }

        return this.diagnosisService.create(data);
      })
    ).subscribe({
      next: res => {
        this.form.markAsPristine();
        this.form.markAsUntouched();

        this.toastrService.success(this.translate.instant(`authenticated.administration.diagnoses.${this.mode}.success`));
        this.diagnosis = res.data;

        if (this.mode === EditModeEnum.CREATE) {
          this.router.navigateByUrl(`/administration/diagnoses/${this.diagnosis!.id}`).then();
        }

        this.mode = this.diagnosis?.id ? EditModeEnum.EDIT : EditModeEnum.CREATE;
        this.cdr.markForCheck();
      },
      error: err => {
        if (err instanceof NotEnoughPermissionsError) {
          this.toastrService.error(this.translate.instant('misc.not_enough_permissions_error'));
        } else if (err instanceof HttpErrorResponse) {
          this.form.markAllAsTouched();
          this.form.updateValueAndValidity({onlySelf: false, emitEvent: true});
          this.formErrorService.mapErrorToForm(this.form, err);
        } else {
          this.toastrService.error(this.translate.instant(`authenticated.administration.diagnoses.${this.mode}.error`));
        }
      },
    })

    this.cdr.markForCheck();
  }

  public delete() {
    if (!this.userSessionService.hasAllPermission([PERMISSION.ADMINISTRATION_DIAGNOSIS_DELETE_ALL])) {
      this.toastrService.error(this.translate.instant('misc.not_enough_permissions_error'));
    }

    if (this.diagnosis?.id) {
      this.diagnosisService.delete(this.diagnosis.id).subscribe({
        next: () => {
          this.toastrService.success(this.translate.instant('authenticated.administration.diagnoses.delete.success'));
          this.router.navigateByUrl('/administration/diagnoses').then();
        },
        error: () => {
          this.toastrService.error(this.translate.instant('authenticated.administration.diagnoses.delete.error'));
        }
      });
    }
  }
}

export default EditComponent
